import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import { ArrowLeft, Receipt, CheckCircle2, XCircle, Clock, Mail } from 'lucide-react';

const RefundPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2" data-testid="refund-logo-link">
              <span className="text-xl font-bold text-foreground">VibeSprint</span>
              <span className="text-[10px] px-1.5 py-0 border border-orange-500/50 text-orange-600 uppercase tracking-wider font-semibold rounded-md">Alpha</span>
            </a>
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="refund-back-button">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-full bg-primary/10 p-2.5">
            <Receipt className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-4xl font-bold">Refund Policy</h1>
        </div>
        <p className="text-muted-foreground mb-8">Last updated: January 2026</p>

        {/* Quick summary card */}
        <Card className="p-6 mb-10 border-primary/30 bg-primary/5" data-testid="refund-summary-card">
          <h2 className="text-lg font-semibold mb-3">The short version</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Annual plans</p>
                <p className="text-sm text-muted-foreground">14-day money-back guarantee, no questions asked.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Monthly plans</p>
                <p className="text-sm text-muted-foreground">Cancel anytime. No refund on the current month, but you keep access until it ends.</p>
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              We want VibeSprint to be the right tool for you. If it isn't, we'd rather you got your money back than feel stuck with a tool you don't use. This policy explains exactly how refunds work, so there are no surprises.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Annual subscriptions — 14-day money-back guarantee</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you purchase an annual plan (Mini, Basic, or Pro), you are eligible for a <strong>full refund within 14 days of your initial purchase</strong>. We don't ask for a reason — just email us and we'll process it.
            </p>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border">
              <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold mb-1">Eligible for full refund:</p>
                <ul className="text-muted-foreground space-y-1 list-disc pl-5">
                  <li>First-time annual subscription, within 14 days of purchase</li>
                  <li>Refund issued to the original payment method within 5–10 business days</li>
                  <li>Subscription is cancelled immediately and access ends on the refund date</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Monthly subscriptions</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Monthly plans are billed in advance for one month at a time. Because of the short commitment, monthly subscriptions are <strong>not eligible for refunds</strong>, but you can cancel anytime.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              When you cancel, you'll continue to have full access until the end of your current billing month — no early cut-off.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Renewals</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Annual subscriptions automatically renew unless you cancel. We send a reminder email <strong>7 days before</strong> your renewal date so you have time to cancel if you no longer need the service.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              If you were charged for a renewal you didn't intend and cancel within <strong>7 days of the renewal charge</strong>, we'll refund the renewal in full as a courtesy. After 7 days, renewals are non-refundable but you can cancel future renewals from your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. When refunds are not available</h2>
            <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/40 border">
              <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                <li>Monthly subscriptions, after the billing cycle has started</li>
                <li>Annual subscriptions after the 14-day window has elapsed</li>
                <li>Accounts terminated for violation of our <a href="/terms" className="text-primary hover:underline">Terms of Service</a></li>
                <li>Add-on AI analyses purchased mid-cycle (these are consumed on-demand)</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. How to request a refund</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Email <a href="mailto:support@vibesprint.ai" className="text-primary hover:underline">support@vibesprint.ai</a> with the subject line "Refund Request" and include:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1.5 mb-4">
              <li>The email address on your VibeSprint account</li>
              <li>The date of purchase and the plan you signed up for</li>
              <li>Optionally: any feedback on why you're cancelling — it helps us improve</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              We respond to refund requests within <strong>1 business day</strong>. Once approved, your bank may take 5–10 business days to reflect the refund on your statement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Chargebacks</h2>
            <p className="text-muted-foreground leading-relaxed">
              Please contact us before initiating a chargeback with your bank. We're a small indie team — chargebacks cost us significant fees and processing time, and we're happy to resolve any issue directly and quickly. Accounts with unresolved chargebacks may be suspended.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Changes to this policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this refund policy from time to time. Any changes apply only to purchases made after the change is published, never retroactively.
            </p>
          </section>

          {/* Contact CTA */}
          <Card className="p-6 text-center mt-12">
            <Mail className="h-8 w-8 text-primary mx-auto mb-3" />
            <h3 className="text-xl font-semibold mb-2">Need to request a refund?</h3>
            <p className="text-muted-foreground mb-4">
              We respond within 1 business day. No long forms, no ticketing system.
            </p>
            <Button
              onClick={() => (window.location.href = 'mailto:support@vibesprint.ai?subject=Refund%20Request')}
              className="btn-cta"
              data-testid="refund-contact-button"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email support@vibesprint.ai
            </Button>
          </Card>
        </div>
      </main>

      <Footer variant="minimal" />
    </div>
  );
};

export default RefundPolicyPage;
